import React, { useState } from "react";

/** Step 1: dry-run preview. Step 2: type DELETE to confirm. */
export function DeleteByConditionConfirmModal({
  preview,
  collectionName,
  filter,
  onCancel,
  onConfirm,
  confirming,
}) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");

  if (!preview) return null;

  const canDelete = confirmText === "DELETE" && !confirming;

  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-2xl">
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-lg font-bold">
            {step === 1 ? "Dry run — Delete by condition" : "Confirm deletion"}
          </h2>
          <p className="text-sm text-slate-500">
            {step === 1
              ? "Review matches. Nothing is deleted until you confirm."
              : 'Type DELETE below to enable the delete button.'}
          </p>
        </div>

        <div className="space-y-3 p-4 text-sm">
          <div className="rounded-lg bg-slate-50 p-3 font-mono text-xs">
            <p>
              <strong>Collection:</strong> {collectionName}
            </p>
            <p>
              <strong>Condition:</strong> {filter.field} {filter.op} &quot;{filter.value}&quot;
            </p>
          </div>

          <p>
            <strong>Matching documents:</strong> {preview.affectedDocuments.toLocaleString()}
          </p>
          <p>
            <strong>Estimated batches:</strong> {preview.estimatedBatches} (max 500 docs/batch)
          </p>
          <p>
            <strong>Estimated time:</strong> ~{preview.estimatedSeconds}s
          </p>

          {preview.warnings?.length > 0 && (
            <div className="rounded-lg bg-amber-50 p-3 text-amber-900">
              {preview.warnings.map((w, i) => (
                <p key={i}>⚠ {w}</p>
              ))}
            </div>
          )}

          <div>
            <p className="mb-1 font-semibold">First 20 matching document IDs</p>
            <div className="max-h-40 overflow-auto rounded-lg border border-slate-200 bg-white p-2 font-mono text-xs">
              {preview.previewIds?.length ? (
                preview.previewIds.map((id) => <div key={id}>{id}</div>)
              ) : (
                <p className="text-slate-500">No matches</p>
              )}
            </div>
          </div>

          {step === 2 && (
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase text-slate-600">
                Type DELETE to confirm
              </label>
              <input
                className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="DELETE"
                autoComplete="off"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 p-4">
          <button type="button" className="fa-btn" onClick={onCancel} disabled={confirming}>
            Cancel
          </button>
          {step === 1 ? (
            <button
              type="button"
              className="fa-btn fa-btn-primary"
              disabled={preview.affectedDocuments === 0}
              onClick={() => setStep(2)}
            >
              Continue to confirmation
            </button>
          ) : (
            <button
              type="button"
              className="fa-btn fa-btn-danger"
              disabled={!canDelete}
              onClick={onConfirm}
            >
              {confirming ? "Starting…" : "Delete matching documents"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export function DeleteProgressModal({ progress }) {
  if (!progress) return null;

  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-md">
        <div className="p-6 text-center">
          <div className="mb-4 inline-block h-10 w-10 animate-spin rounded-full border-4 border-indigo-200 border-t-indigo-600" />
          <h2 className="text-lg font-bold">Deleting…</h2>
          <p className="mt-2 text-sm text-slate-600">{progress.message}</p>
          {progress.phase === "deleting" && progress.batch != null && (
            <p className="mt-1 font-mono text-sm text-indigo-700">
              Batch {progress.batch} of {progress.totalBatches}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export function DeleteResultModal({ result, onClose }) {
  if (!result) return null;

  return (
    <div className="fa-modal-backdrop">
      <div className="fa-modal max-w-md">
        <div className="border-b border-slate-200 p-4">
          <h2 className="text-lg font-bold">Delete complete</h2>
        </div>
        <div className="space-y-2 p-4 text-sm">
          <p>
            <strong>Deleted:</strong> {result.deleted.toLocaleString()} documents
          </p>
          <p>
            <strong>Failed:</strong> {result.failed.toLocaleString()} documents
          </p>
          <p>
            <strong>Time:</strong> {result.seconds} seconds
          </p>
          {result.backups?.length > 0 && (
            <p className="text-emerald-700">
              <strong>Backed up:</strong> {result.backups.length} record(s) in firestore_backups
            </p>
          )}
          {result.errors?.length > 0 && (
            <div className="mt-2 rounded border border-red-200 bg-red-50 p-2 text-xs text-red-900">
              <p className="font-semibold">Error details</p>
              {result.errors.map((e, i) => (
                <p key={i} className="mt-1 font-mono break-all">{e}</p>
              ))}
            </div>
          )}
          {result.failed > 0 && result.failedIds?.length > 0 && (
            <div className="mt-2 max-h-24 overflow-auto rounded border border-red-200 bg-red-50 p-2 font-mono text-xs">
              {result.failedIds.slice(0, 10).map((id) => (
                <div key={id}>{id}</div>
              ))}
            </div>
          )}
        </div>
        <div className="flex justify-end border-t border-slate-200 p-4">
          <button type="button" className="fa-btn fa-btn-primary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
